import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { rootUrl } from "../App";
import { useDispatch } from "react-redux";
import { setInnerLoaderFalse, setInnerLoaderTrue } from "../Redux/loaderSlice";
import { addDataIntoCache, getCacheData } from "../Redux/cacheSlice";

const defaultLogo = process.env.PUBLIC_URL + "/app/default-logo.png";

export const useStatics = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const [mirrorLinkData, setMirrorLinkData] = useState({});
    const [logo, setLogo] = useState({});
    const [sliderImgList, setSliderImgList] = useState(null);
    const [homeBannersList, setHomeBannersList] = useState(null);
    const [loginBannersList, setLoginBannersList] = useState(null);
    const [welcomePageData, setWelcomePageData] = useState(null);
    const [appBG, setAppBG] = useState([]);
    const [promotionalsBG, setPromotionalsBG] = useState([]);
    const [percentage, setPercentage] = useState(((new Date())?.getSeconds()) % 100);
    const [progressBar, setProgressBar] = useState(true);
    const [getStartedData, setGetStartedData] = useState([]);
    const [getStartedDataState, setGetStartedDataState] = useState(true);
    const [getStartRef, setGetStartRef] = useState(localStorage.getItem("getStartAccess"));

    const [liveChatCode, setLiveChatCode] = useState(null);

    const setTrueStart = () => {
        setGetStartRef(localStorage.getItem("getStartAccess"));
    };

    const pageName = location?.pathname.split("/")[1] || "";

    // Helper to validate logo
    const getValidLogoUrl = (linkLogo) => {
        if (!linkLogo || linkLogo === "/media/") return defaultLogo;
        if (!linkLogo.toLowerCase().match(/\.(png|jpe?g|webp|gif|svg)$/)) return defaultLogo;
        return rootUrl + linkLogo;
    };

    // Fetch mirror-links (excluding live chat code)
    useEffect(() => {
        const cacheExpirationThreshold = 6 * 60 * 60 * 1000; // 6 hours
        const cacheKey = "mirrorLinksCache";
        const cacheUrl = `${rootUrl}/mirror-links/`;

        const fetchMirrorLinksData = async () => {
            try {
                const cachedData = await getCacheData(cacheKey, cacheUrl);
                if (cachedData) {
                    const cachedTime = new Date(cachedData.updateDate);
                    if (new Date() - cachedTime < cacheExpirationThreshold) {
                        const logoUrl = getValidLogoUrl(cachedData.data?.link_logo);
                        setLogo(logoUrl);
                        document.title = cachedData.data?.web_title || document.title;
                        setMirrorLinkData(cachedData.data);
                        setLiveChatCode(cachedData.data?.live_chat_code);
                        return;
                    }
                }
            } catch (err) {
                console.warn("mirror-links cache error:", err);
            }

            try {
                const response = await fetch(cacheUrl);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();

                if (data?.status === "ok") {
                    const logoUrl = getValidLogoUrl(data.data?.link_logo);
                    setLogo(logoUrl);
                    document.title = data.data?.web_title || document.title;
                    setMirrorLinkData(data.data);
                    setLiveChatCode(data.data?.live_chat_code);

                    try {
                        addDataIntoCache(cacheKey, cacheUrl, {
                            data: data.data,
                            updateDate: new Date(),
                        });
                    } catch (cacheErr) {
                        console.warn("addDataIntoCache error:", cacheErr);
                    }
                }
            } catch (fetchErr) {
                console.error("Error fetching mirror links:", fetchErr);
            }
        };

        fetchMirrorLinksData();
    }, []);

    // Statics / banners
    useEffect(() => {
        const fetchStaticsData = async () => {
            dispatch(setInnerLoaderTrue());

            const cacheExpirationThreshold = 24 * 60 * 60 * 1000; // 24 hours

            const [
                cacheDataOfAppBG,
                cacheDataOfPromotionalsBG,
                cacheDataOfSliders,
                cacheDataOfHomeBanners,
                cacheDataOfLoginBanners,
                cacheDataOfWelcomePage,
            ] = await Promise.all([
                getCacheData("appBGCache", `${rootUrl}/statics/banners/app_background_image`),
                getCacheData("promotionalsBGCache", `${rootUrl}/statics/banners/download_app_bg`),
                getCacheData("slidersCache", `${rootUrl}/statics/banners/`),
                getCacheData("homeBannersCache", `${rootUrl}/statics/banner-promition/`),
                getCacheData("loginBannersCache", `${rootUrl}/statics/banner-login/`),
                getCacheData("welcomePageCache", `${rootUrl}/statics/banner-promition/`),
            ]);

            // ---------------------------------------------
            // ✅ PLACE EARLY EXIT CHECK HERE
            // ---------------------------------------------
            const now = Date.now();

            const allCacheExist =
                cacheDataOfAppBG &&
                cacheDataOfPromotionalsBG &&
                cacheDataOfSliders &&
                cacheDataOfHomeBanners &&
                cacheDataOfLoginBanners &&
                cacheDataOfWelcomePage;

            const allCacheValid =
                allCacheExist &&
                now - new Date(cacheDataOfAppBG.updateDate) < cacheExpirationThreshold &&
                now - new Date(cacheDataOfPromotionalsBG.updateDate) < cacheExpirationThreshold &&
                now - new Date(cacheDataOfSliders.updateDate) < cacheExpirationThreshold &&
                now - new Date(cacheDataOfHomeBanners.updateDate) < cacheExpirationThreshold &&
                now - new Date(cacheDataOfLoginBanners.updateDate) < cacheExpirationThreshold &&
                now - new Date(cacheDataOfWelcomePage.updateDate) < cacheExpirationThreshold;

            if (allCacheValid) {
                // Load all cached states at once
                setAppBG(cacheDataOfAppBG.data);
                setPromotionalsBG(cacheDataOfPromotionalsBG.data);

                const sliderData = cacheDataOfSliders.data.filter(
                    (item) => item.page_list_id?.name === (pageName || "home")
                );
                setSliderImgList(sliderData);

                setHomeBannersList(cacheDataOfHomeBanners.data);
                setLoginBannersList(cacheDataOfLoginBanners.data);
                setWelcomePageData(cacheDataOfWelcomePage.data);

                dispatch(setInnerLoaderFalse());
                return; // ⛔ STOP — prevent fetch
            }
            // ---------------------------------------------
            // ❗ After this point → perform your normal fetch
            // ---------------------------------------------

            try {
                const [slidersRes, homeBannersRes] = await Promise.all([
                    fetch(`${rootUrl}/statics/banners/`).then((r) => r.json()),
                    fetch(`${rootUrl}/statics/banner-promition/`).then((r) => r.json()),
                ]);

                if (slidersRes?.status === "ok" && slidersRes?.data?.length) {
                    const sliderData = slidersRes.data.filter((item) => item.page_list_id?.name === (pageName || "home"));
                    setSliderImgList(sliderData);
                    addDataIntoCache("slidersCache", `${rootUrl}/statics/banners/`, { data: slidersRes.data, updateDate: new Date() });

                    const bgData = slidersRes.data.filter((item) => item.page_list_id?.name === "app_background_image");
                    setAppBG(bgData);
                    addDataIntoCache("appBGCache", `${rootUrl}/statics/banners/app_background_image`, { data: bgData, updateDate: new Date() });

                    const promotionalsData = slidersRes.data.filter((item) => item.page_list_id?.name === "download_app_bg");
                    setPromotionalsBG(promotionalsData);
                    addDataIntoCache("promotionalsBGCache", `${rootUrl}/statics/banners/download_app_bg`, { data: promotionalsData, updateDate: new Date() });
                }

                if (homeBannersRes?.status === "ok" && homeBannersRes?.data?.length) {
                    const filteredHomeBanners = homeBannersRes.data.filter((item) => item.is_active && item.slider_show);
                    const filteredLoginBanners = homeBannersRes.data.filter((item) => item.is_active && item.login);
                    const foundWelcomePage = homeBannersRes.data.find((item) => item.is_active && item.welcome_page);
                    setHomeBannersList(filteredHomeBanners);
                    setLoginBannersList(filteredLoginBanners);
                    setWelcomePageData(foundWelcomePage);

                    addDataIntoCache("homeBannersCache", `${rootUrl}/statics/banner-promition/`, { data: filteredHomeBanners, updateDate: new Date() });
                    addDataIntoCache("loginBannersCache", `${rootUrl}/statics/banner-login/`, { data: filteredLoginBanners, updateDate: new Date() });
                    addDataIntoCache("welcomePageCache", `${rootUrl}/statics/banner-promition/`, { data: foundWelcomePage, updateDate: new Date() });
                }
            } catch (err) {
                console.error("statics fetch error:", err);
            } finally {
                dispatch(setInnerLoaderFalse());
            }
        };

        fetchStaticsData();
    }, [pageName, dispatch]);

    // Get-started effect
    useEffect(() => {
        const fetchGetStartedData = async () => {
            const cacheDataOfGetStartedBanner = await getCacheData("getStartedCache", `${rootUrl}/statics/get-started/banners/`);

            if (cacheDataOfGetStartedBanner) {
                const currentTime = new Date();
                const cachedTimeStringOfGetStartedBanner = cacheDataOfGetStartedBanner?.updateDate;
                const cachedTimeOfGetStartedBanner = new Date(cachedTimeStringOfGetStartedBanner);
                const cacheExpirationThreshold = 24 * 60 * 60 * 1000; // 24h
                const timeDifferenceOfGetStartedBanner = currentTime - cachedTimeOfGetStartedBanner;

                if (timeDifferenceOfGetStartedBanner < cacheExpirationThreshold) {
                    setGetStartedData(cacheDataOfGetStartedBanner?.data);
                    setGetStartedDataState(false);
                    return null;
                }
            }

            try {
                let response = await fetch(`${rootUrl}/statics/get-started/banners/`);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                let data = await response.json();
                if (data?.status === "ok") {
                    if (!data?.data?.length) {
                        localStorage.setItem("getStartAccess", true);
                    }
                    addDataIntoCache("getStartedCache", `${rootUrl}/statics/get-started/banners/`, { data: data?.data, updateDate: new Date() });
                    setGetStartedData(data?.data);
                }
            } catch (error) {
                console.error("Error fetching get-started data:", error);
            } finally {
                setGetStartedDataState(false);
            }
        };
        fetchGetStartedData();
    }, []);

    // Progress bar logic
    useEffect(() => {
        const progressTimer = 500;
        if (getStartedDataState === false && logo && appBG?.length > 0) {
            if (percentage === 100) {
                setTimeout(() => {
                    setProgressBar(false);
                }, progressTimer);
            } else {
                setPercentage(100);
            }
        } else {
            setTimeout(() => {
                setPercentage(80);
            }, progressTimer);
        }
    }, [logo, appBG, percentage, getStartedDataState]);

    return {
        mirrorLinkData,
        logo,
        welcomePageData,
        loginBannersList,
        homeBannersList,
        sliderImgList,
        appBG,
        promotionalsBG,
        progressBar,
        percentage,
        getStartedData,
        getStartedDataState,
        getStartRef,
        setTrueStart,
        liveChatCode,
    };
};